import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FiFileText,
  FiGift,
  FiCalendar,
  FiBriefcase,
  FiShoppingBag,
  FiGrid,
  FiArrowRight,
  FiCheckCircle,
  FiStar,
  FiPlus,
  FiEdit3,
  FiFolder,
  FiTool,
  FiBookOpen,
  FiHeart,
  FiLayout,
  FiClipboard,
} from "react-icons/fi";
import SaaSStatCard from "../../common/saas/SaaSStatCard";
import {
  getCustomerLeads,
  getMeetingReminders,
  getRewardPointsCount,
} from "../../../api/customer/customerFeatureApis";
import { getUserLocation } from "../../../api/customer/overviewApis";
import useAsyncData from "./sections/useAsyncData";
import {
  PropertiesNearbySection,
  PostsNearbySection,
  PromotionsSection,
  InteriorDesignSection,
  HomeServicesSection,
} from "./sections/OverviewSections";
import "./CustomerDashboardView.css";
import "./sections/OverviewSections.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Items the customer created in My Space (same endpoint the mobile My Space screen uses)
const getMySpaceCount = async (token) => {
  const headers = { Authorization: `Bearer ${token}` };
  const query = (search_type) =>
    axios.post(`${BASE_URL}/property/list/my/space`, { search_type, page_number: 1, page_size: 100, order_by: "DESC" }, { headers });
  const [properties, posts] = await Promise.all([query("Property"), query("Post")]);
  return (properties.data?.properties || []).length + (posts.data?.leads || []).length;
};

// Marketplace hub: every tile is a real, working customer route
const HUB_TILES = [
  { label: "Home Services", description: "Plumbers, electricians & more", icon: FiTool, to: "/customer/services", tint: "blue" },
  { label: "Local Bazaar", description: "Shops and vendors near you", icon: FiShoppingBag, to: "/customer/bazaar", tint: "green" },
  { label: "Desire Rewards", description: "Points, history & redeem", icon: FiGift, to: "/customer/rewards", tint: "amber" },
  { label: "Visit Reminders", description: "Site visits and calls", icon: FiCalendar, to: "/customer/reminders", tint: "purple" },
  { label: "Khatabook", description: "Track credits, debits and balances", icon: FiBookOpen, to: "/customer/tools/khatabook", tint: "blue" },
  { label: "Care Leads", description: "Health & care requests", icon: FiHeart, to: "/customer/healthians", tint: "red" },
  { label: "Interior Design", description: "Design consultations & trusted experts", icon: FiLayout, to: "/customer/interior-design", tint: "purple" },
];

export const CustomerDashboardView = () => {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  const [greeting, setGreeting] = useState("Good Morning");

  // Derive Display Name
  const userObj = loggedInUser?.user || loggedInUser?.userDetails || loggedInUser;
  const userId = userObj?.id;
  const rawFirstName = userObj?.first_name || userObj?.firstName;
  const rawLastName = userObj?.last_name || userObj?.lastName;
  const rawName = userObj?.name || userObj?.customer_name;
  const fullName = [rawFirstName, rawLastName].filter(Boolean).join(" ") || rawName;
  const displayName = fullName && fullName.trim() && !fullName.toLowerCase().includes("null")
    ? fullName.trim()
    : "there";

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 18) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, []);

  /* ---------------- stat cards + rewards banner ---------------- */
  const enabled = Boolean(token);
  const mySpace = useAsyncData(() => getMySpaceCount(token), [token], enabled);
  const leads = useAsyncData(
    async () => {
      const data = await getCustomerLeads(token, { page_number: 1, page_size: 5 });
      const list = data?.customerLeads || data?.leads || [];
      return data?.totalRecord || list.length || 0;
    },
    [token],
    enabled
  );
  const reminders = useAsyncData(
    async () => {
      const data = await getMeetingReminders(token, { pageNumber: 1, pageSize: 5 });
      const list = data?.reminders || data?.data || [];
      return data?.totalRecord || list.length || 0;
    },
    [token],
    enabled
  );
  const rewards = useAsyncData(
    async () => {
      const res = await getRewardPointsCount(token);
      if (res?.statusType !== "SUCCESS") throw new Error(res?.text || "Unable to load rewards");
      return res.rewardPoints || 0;
    },
    [token],
    enabled
  );

  const statValue = (state, format = (v) => String(v)) =>
    state.error ? "—" : state.loading || state.data === null ? "…" : format(state.data);
  const rewardPoints = rewards.data;

  /* ---------------- location for the "nearby" sections (mobile: admin/user/id) ---------------- */
  const locationState = useAsyncData(() => getUserLocation(token, userId), [token, userId], enabled);
  const location = useMemo(
    () => locationState.data || { state: "", city: "", areas: [] },
    [locationState.data]
  );
  // If the profile lookup fails the sections still load, falling back to "everything" like mobile
  const locationReady = !locationState.loading;

  return (
    <div className="ov-page">
      {/* 1. Header card */}
      <div className="customer-dash-welcome">
        <div className="customer-dash-welcome__left">
          <div className="customer-dash-welcome__badge">
            <FiCheckCircle size={13} /> Valued Homebuyer Workspace
          </div>
          <h1 className="customer-dash-welcome__title">
            {greeting}, {displayName}!
          </h1>
          <p className="customer-dash-welcome__subtitle">
            Search verified properties, track your inquiries, schedule visits, and earn Desire reward points.
          </p>
        </div>
        <div className="customer-dash-welcome__actions">
          <button type="button" className="btn btn-primary btn-sm px-3 py-2 rounded-3 fw-medium shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate("/property/add-property")}>
            <FiPlus size={15} />
            <span>Create Property</span>
          </button>
          <button type="button" className="btn btn-outline-primary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate("/post/add-post")}>
            <FiEdit3 size={15} />
            <span>Create Post</span>
          </button>
          <button type="button" className="btn btn-outline-primary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate("/customer/leads/new")}>
            <FiClipboard size={15} />
            <span>Create Lead</span>
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate("/customer/myspace")}>
            <FiFolder size={15} className="text-secondary" />
            <span>MySpace</span>
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm px-3 py-2 rounded-3 fw-medium bg-white shadow-sm d-inline-flex align-items-center gap-2" onClick={() => navigate("/customer/properties")}>
            <FiBriefcase size={15} className="text-secondary" />
            <span>Browse Properties</span>
          </button>
        </div>
      </div>

      {/* 2. Stat cards (equal height, whole card is a link) */}
      <div className="ov-stats">
        <SaaSStatCard
          title="MySpace Content"
          value={statValue(mySpace, (n) => `${n} Items`)}
          icon={<FiFolder />}
          color="blue"
          subtitle="Properties & posts"
          onClick={() => navigate("/customer/myspace")}
        />
        <SaaSStatCard
          title="My Inquiries"
          value={statValue(leads)}
          icon={<FiFileText />}
          color="green"
          subtitle="Active requirements"
          onClick={() => navigate("/customer/leads")}
        />
        <SaaSStatCard
          title="Desire Rewards"
          value={statValue(rewards, (n) => `${n.toLocaleString("en-IN")} Pts`)}
          icon={<FiGift />}
          color="amber"
          subtitle={rewards.error ? "Unable to load" : rewardPoints == null ? "Loading" : `Redeem value ₹${rewardPoints.toLocaleString("en-IN")}`}
          onClick={() => navigate("/customer/rewards")}
        />
        <SaaSStatCard
          title="Scheduled Meetings"
          value={statValue(reminders)}
          icon={<FiCalendar />}
          color="purple"
          subtitle="Site visits & calls"
          onClick={() => navigate("/customer/reminders")}
        />
      </div>

      {/* 3. Rewards banner (real balance from /customer/rewards/count) */}
      <div className="customer-dash-rewards-banner">
        <div className="customer-dash-rewards-banner__header">
          <span className="customer-dash-rewards-banner__badge">
            <FiStar fill="#fbbf24" color="#fbbf24" size={14} /> Desire Rewards
          </span>
        </div>
        {rewards.error ? (
          <>
            <p className="customer-dash-rewards-banner__points-label">Unable to load your reward balance right now.</p>
            <div className="mb-3">
              <button type="button" className="btn btn-outline-light btn-sm" onClick={rewards.retry}>
                Retry
              </button>
            </div>
          </>
        ) : rewardPoints == null ? (
          <div className="customer-dash-rewards-banner__balance" aria-busy="true">
            <span style={{ opacity: 0.5 }}>…</span>
          </div>
        ) : (
          <>
            <div className="customer-dash-rewards-banner__balance">
              {rewardPoints.toLocaleString("en-IN")}{" "}
              <span style={{ fontSize: "1rem", fontWeight: 600 }}>REWARD POINTS</span>
            </div>
            <p className="customer-dash-rewards-banner__points-label">
              {rewardPoints > 0
                ? `Redeem value ₹${rewardPoints.toLocaleString("en-IN")}. Rewards are earned through activities and referrals.`
                : "No rewards yet. Rewards are earned through activities and referrals — open Rewards to see how."}
            </p>
          </>
        )}

        <div className="d-flex flex-wrap gap-2 align-items-center">
          <button type="button" className="customer-dash-rewards-banner__btn" onClick={() => navigate("/customer/rewards")}>
            <span>View Rewards</span>
          </button>
          <button type="button" className="btn btn-outline-light btn-sm px-3 py-2 rounded-2 fw-medium d-inline-flex align-items-center gap-1" onClick={() => navigate("/customer/profile")}>
            <span>View Profile & Email Verification</span>
            <FiArrowRight size={14} />
          </button>
        </div>
      </div>

      {/* 4. Marketplace hub */}
      <div className="ov-section" role="region" aria-labelledby="ov-hub-title">
        <div className="ov-section__head">
          <div>
            <h2 className="ov-section__title" id="ov-hub-title">
              <FiGrid className="text-primary me-2" aria-hidden="true" />
              Customer Marketplace Hub
            </h2>
            <p className="ov-section__desc">Instant access to your home services and everyday tools.</p>
          </div>
        </div>
        <div className="ov-hub ov-hub--7">
          {HUB_TILES.map(({ label, description, icon: Icon, to, tint }) => (
            <Link key={label} to={to} className={`ov-tile ov-tint--${tint}`}>
              <span className="ov-tile__icon" aria-hidden="true">
                <Icon size={22} />
              </span>
              <span className="ov-tile__label">{label}</span>
              <span className="ov-tile__desc">{description}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* 5. Optional sections in order; each one removes itself when it has no data */}
      <PropertiesNearbySection token={token} location={location} locationReady={locationReady} />
      <PostsNearbySection token={token} location={location} locationReady={locationReady} />
      <PromotionsSection token={token} />
      <InteriorDesignSection token={token} />
      <HomeServicesSection token={token} />
    </div>
  );
};

export default CustomerDashboardView;
